import mapValues from 'lodash.mapvalues'
import { ContentStateInterface, ContentStateProperty, DropdownOptionSelection } from '../../@types/types'
import {
  ScreenSchema,
  ScreenSchemaParsed,
  ScreenValidationResult,
  ValidationResult,
  ValidationRule,
  ValidationSchema,
  ValidationSchemaParsed,
  ValidationStructure,
} from '../../@types/validation-types'
import { noOpValidators } from './flow/defaultSchemas'
import { AddressFeatureState } from '../../@types/state-types'
import { InitState } from '../../redux/reducers/root/initialState'
import { useAppSelector } from '../../app/hooks'
import { getPropertyValue } from '../../utils/types-utils'
import { validateContainsEmail } from './rules/validationFunctions'

export const getValidationInvalid = <T extends Record<string, R>, R extends ScreenValidationResult<any>>(
  validators: T,
  mapFunction: (value: R) => ValidationStructure
): boolean =>
  Object.entries(validators)
    .map(([_, value]) => mapFunction(value as R))
    .filter((it) => it !== undefined)
    .flatMap((it) => Object.entries(it).map(([_, value]) => value))
    .some((it) => it?.length)

export type ValidationData = {
  property: ContentStateProperty
  value: string
  insertionData: ContentStateInterface
}

export const executeValidators = (o: ValidationData, validators: ValidationRule[]): string[] => {
  const result: string[] = []

  for (const validator of validators) {
    const validationResult = validator(o.value, o.property, o.insertionData)
    validationResult && result.push(validationResult)
  }

  const emailValidationResult = validateContainsEmail(o.value, o.property)
  if (emailValidationResult) {
    result.push(emailValidationResult)
  }

  return result
}

function extractValue(property: ContentStateProperty, insertionData: ContentStateInterface): string {
  const propertyValue = getPropertyValue(insertionData, property)
  if (property == 'price') {
    return propertyValue === undefined ? '' : String(propertyValue)
  }
  const potentialDropdownValue = (propertyValue as DropdownOptionSelection)?.value
  if (potentialDropdownValue !== undefined) {
    return potentialDropdownValue || ''
  } else if (propertyValue) {
    return String(propertyValue)
  }
  return ''
}

export const getValidationSchema: (
  validationSchemaFactory: ValidationSchema,
  insertionData: ContentStateInterface,
  addressFeature: AddressFeatureState
) => ValidationSchemaParsed = (validationSchemaFactory, insertionData, addressFeature) =>
  mapValues(validationSchemaFactory, (screenSchema) => {
    if (!screenSchema) {
      return screenSchema
    }
    return typeof screenSchema === 'function'
      ? screenSchema(insertionData, addressFeature)
      : getValidationSchemaForScreen(screenSchema, insertionData, addressFeature)
  })

function getValidationSchemaForScreen(
  screenSchema: ScreenSchema,
  insertionData: ContentStateInterface,
  addressFeature: AddressFeatureState
): ScreenSchemaParsed {
  return mapValues(screenSchema, (propertyValidators) => {
    return !propertyValidators || Array.isArray(propertyValidators)
      ? propertyValidators
      : propertyValidators(insertionData, addressFeature)
  })
}

// Don't call this method directly, use runValidation
export const validate = (
  insertionData: ContentStateInterface,
  addressFeature: AddressFeatureState,
  validationSchemaFactory: ValidationSchema
): ValidationResult => {
  const validationSchema = getValidationSchema(validationSchemaFactory, insertionData, addressFeature)
  const p = (
    t: ContentStateProperty
  ): { property: ContentStateProperty; value: string; insertionData: ContentStateInterface } => ({
    property: t,
    value: extractValue(t, insertionData),
    insertionData: insertionData,
  })

  return {
    ...noOpValidators,
    ...mapValues(validationSchema, (screenSchema) => {
      return {
        fields: mapValues(screenSchema, (rules: ValidationRule[], property: ContentStateProperty) => {
          return executeValidators(p(property), rules)
        }),
        validated: true,
      }
    }),
  }
}

export const useGetFinalParsedValidationSchema = (): ValidationSchemaParsed => {
  const validationSchema = useAppSelector((state: InitState) => state.flowFeature.flowInformation.validationSchema)
  // TODO PPA-8679: Using insertionData here can lead to errors because the insertionData in redux is not the up to date one.
  const { insertionData } = useAppSelector((state: InitState) => state.insertionFeature)
  const addressFeature = useAppSelector((state: InitState) => state.addressFeature)
  return getValidationSchema(validationSchema, insertionData, addressFeature)
}
